import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { getSession, signInWithGoogleMock, signOut } from '../../shared/api/auth'
import type { User } from '../../shared/api/types'

interface AuthContextValue {
  user: User | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
  /** Sign in. Pass a real Google credential (id_token) when available;
   *  omit it to use the demo / dev-fallback sign-in. */
  signInWithGoogle: (idToken?: string) => Promise<User>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthContextValue['status']>('loading')

  useEffect(() => {
    let active = true
    getSession()
      .then((session) => {
        if (!active) return
        setUser(session)
        setStatus(session ? 'authenticated' : 'unauthenticated')
      })
      .catch(() => {
        if (!active) return
        setStatus('unauthenticated')
      })
    return () => {
      active = false
    }
  }, [])

  const signInWithGoogle = useCallback(async (idToken?: string) => {
    const session = await signInWithGoogleMock(idToken)
    setUser(session)
    setStatus('authenticated')
    return session
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    setUser(null)
    setStatus('unauthenticated')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, signInWithGoogle, logout }),
    [user, status, signInWithGoogle, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
